import { useEffect, useState } from 'react';
import { ArrowRight, Award, BadgeCheck, ClipboardCheck, Droplet, HeartHandshake, Leaf, Milk, Sprout, Truck, Users } from 'lucide-react';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import { CountUp, Marquee, Reveal } from '../components/Motion';
import { PRODUCT_CATEGORIES, productImage } from '../lib/products';
import { countVisit, photoUrl, useSite, type VisitStats } from '../../utils/site';
import { cn } from '../../utils/cn';

const HIGHLIGHTS = [
  {
    icon: Droplet,
    title: 'Pure & Traceable',
    description: 'Complete traceability from our herd and flock to your table, backed by digital farm management.',
  },
  {
    icon: HeartHandshake,
    title: 'Ethical Animal Care',
    description: 'Every animal on our farm is raised with scientific, humane practices and regular veterinary attention.',
  },
  {
    icon: Award,
    title: 'Trusted Since 2026',
    description: 'A growing team of designated partners and partners behind every product that leaves our farm.',
  },
];

const PARTNER_NAMES = [
  'Babu MG', 'Nagesh Babu BV', 'Vinay Kumar T', 'Ramesh MD', 'Muralidhara MR', 'Nanjunda Reddy K', 'Ramesh Reddy',
];

// How a product reaches you - the farm's own process, in order.
const STEPS = [
  { icon: Sprout, title: 'Raised on our farm', text: 'Healthy animals, natural feed and regular veterinary care.' },
  { icon: Milk, title: 'Collected with care', text: 'Morning and evening, recorded digitally at every step.' },
  { icon: ClipboardCheck, title: 'Checked & packed', text: 'Every batch checked before it leaves the farm.' },
  { icon: Truck, title: 'Delivered to you', text: 'Fresh to your door — traceable back to the farm.' },
];

// Farm sections named in our own story (dairy, poultry, sheep, natural farming).
const FARM_SECTIONS = 4;

export default function Home() {
  const site = useSite();
  const [visits, setVisits] = useState<VisitStats | null>(null);
  useEffect(() => {
    countVisit().then(setVisits).catch(() => setVisits(null)); // the counter never stops the page
  }, []);
  const showVisits = site.visitors_show && visits !== null && visits.views > 0;
  return (
    <section id="home" className="scroll-mt-20">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-green-50 via-surface to-surface">
        <Container className="grid gap-10 py-14 sm:py-20 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-gold-100 px-4 py-1.5 text-sm font-semibold text-brand-gold-700">
              <Leaf className="h-4 w-4" /> {site.hero_badge}
            </p>
            <h1 className="text-4xl font-extrabold leading-tight text-brand-green-900 sm:text-5xl">
              {site.hero_title}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-500">
              {site.hero_text}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/shop"
                className="inline-flex items-center gap-2 rounded-full bg-brand-gold-500 px-6 py-3 text-sm font-semibold text-white shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-gold-600"
              >
                Order Milk Online <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#products"
                className="inline-flex items-center gap-2 rounded-full bg-brand-green-700 px-6 py-3 text-sm font-semibold text-white shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-green-800"
              >
                Explore Products <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand-green-700 px-6 py-3 text-sm font-semibold text-brand-green-700 transition-all hover:-translate-y-0.5 hover:bg-brand-green-50"
              >
                Get in Touch
              </a>
            </div>
          </Reveal>
          <Reveal delay={150} className="relative">
            <div className="overflow-hidden rounded-2xl shadow-popover">
              <img
                src={photoUrl(site, 'home.hero') ?? '/hero/banner.png'}
                alt="Sri KalpaGavya — healthy animals, better food, a greener future. Dairy, poultry and sheep raised on our farm, with fresh milk, eggs and produce."
                className="h-full w-full object-cover"
              />
            </div>
            {/* Two cards: a row under the banner on phones/tablets (so they never
                cover it), floating just off its corners on wide screens. */}
            <div className="mt-4 grid grid-cols-2 gap-3 lg:mt-0 lg:block">
              <div className="skg-float-lg flex items-center gap-3 rounded-xl bg-surface px-3 py-3 shadow-card lg:absolute lg:-bottom-7 lg:-left-8 lg:px-4 lg:shadow-popover">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-green-50"><Milk className="h-5 w-5 text-brand-green-700" /></span>
                <div>
                  <p className="text-sm font-bold text-brand-green-900">Farm-fresh daily</p>
                  <p className="text-xs text-ink-500">Morning &amp; evening</p>
                </div>
              </div>
              <div className="skg-float-lg-delay flex items-center gap-3 rounded-xl bg-surface px-3 py-3 shadow-card lg:absolute lg:-right-8 lg:-top-7 lg:px-4 lg:shadow-popover">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gold-100"><BadgeCheck className="h-5 w-5 text-brand-gold-700" /></span>
                <div>
                  <p className="text-sm font-bold text-brand-green-900">100% traceable</p>
                  <p className="text-xs text-ink-500">Farm to table</p>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Farm in numbers */}
      <section className="border-y border-brand-green-100 bg-surface">
        <Container className={cn('grid grid-cols-2 gap-6 py-10', showVisits ? 'sm:grid-cols-5' : 'sm:grid-cols-4')}>
          {[
            { value: <>Est. 2026</>, label: 'Founded' },
            { value: <CountUp to={FARM_SECTIONS} />, label: 'Farm sections' },
            { value: <CountUp to={PRODUCT_CATEGORIES.length} />, label: 'Product lines' },
            { value: <CountUp to={PARTNER_NAMES.length} suffix="+" />, label: 'Designated partners' },
            ...(showVisits ? [{
              value: <CountUp to={visits!.views} />, label: site.visitors_label || 'Website visits',
              note: `${visits!.today_views.toLocaleString('en-IN')} today · ${visits!.visitors.toLocaleString('en-IN')} visitors`,
            }] : []),
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 100} className={cn('text-center', i === 4 && 'col-span-2 sm:col-span-1')}>
              <p className="text-3xl font-extrabold text-brand-green-800 sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-sm font-medium text-ink-500">{s.label}</p>
              {'note' in s && <p className="mt-0.5 flex items-center justify-center gap-1.5 text-xs text-ink-400"><span className="skg-live-dot h-1.5 w-1.5 rounded-full bg-success-500" />{s.note}</p>}
            </Reveal>
          ))}
        </Container>
      </section>

      {/* Highlights */}
      <section className="py-16 sm:py-20">
        <Container>
          <div className="grid gap-6 sm:grid-cols-3">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={i * 120}>
                <div className="h-full rounded-xl border border-brand-green-100 bg-surface p-6 shadow-card transition-all hover:-translate-y-1 hover:shadow-popover">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-green-50">
                    <h.icon className="h-6 w-6 text-brand-green-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-brand-green-900">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-500">{h.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* From our farm to you - the process, in order */}
      <section className="bg-surface-subtle py-16 sm:py-20">
        <Container>
          <Reveal>
            <SectionHeading eyebrow="How it works" title="From our farm to you" subtitle="Four steps every product goes through before it reaches your home." center />
          </Reveal>
          <div className="relative mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="absolute left-[12%] right-[12%] top-8 hidden h-0.5 bg-gradient-to-r from-brand-green-200 via-brand-gold-300 to-brand-green-200 lg:block" aria-hidden="true" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 150} className="relative text-center">
                <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-green-700 text-white shadow-popover">
                  <s.icon className="h-7 w-7" />
                  <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand-gold-400 text-xs font-bold text-brand-green-950">{i + 1}</span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-brand-green-900">{s.title}</h3>
                <p className="mx-auto mt-1 max-w-[16rem] text-sm text-ink-500">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* About teaser */}
      <section className="py-16 sm:py-20">
        <Container className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-xl shadow-card">
              <img src={photoUrl(site, 'home.overview') ?? '/hero/farm-overview.jpg'} alt="Sri KalpaGavya farm overview" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={150}>
            <SectionHeading
              eyebrow="Our Story"
              title="An integrated, sustainable farming enterprise"
              subtitle="Sri KalpaGavya Dairy and Farms LLP combines responsible dairy farming, poultry, sheep rearing and natural agriculture with modern digital farm management — promoting healthy food, animal welfare, sustainable agriculture and rural prosperity."
            />
            <a
              href="#about"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-green-700 hover:text-brand-green-800"
            >
              Read our full story <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </Container>
      </section>

      {/* Products teaser */}
      <section className="bg-surface-subtle py-16 sm:py-20">
        <Container>
          <Reveal>
            <SectionHeading
              eyebrow="What We Offer"
              title="Our product lines"
              subtitle="Everything we produce comes from our own farm — raised, grown and prepared with the same care, batch after batch."
              center
            />
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {PRODUCT_CATEGORIES.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 120}>
                <div className="group h-full overflow-hidden rounded-xl border border-brand-green-100 bg-surface text-center shadow-card transition-all hover:-translate-y-1 hover:shadow-popover">
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-brand-green-50">
                    <img src={productImage(site, cat.products[0])} alt={cat.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 mx-auto -mb-5 flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold-100 shadow-card">
                      <cat.icon className="h-5 w-5 text-brand-gold-700" />
                    </div>
                  </div>
                  <div className="p-6 pt-7">
                    <h3 className="text-base font-semibold text-brand-green-900">{cat.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-500">{cat.tagline}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              href="#products"
              className="inline-flex items-center gap-2 rounded-full bg-brand-green-700 px-6 py-3 text-sm font-semibold text-white shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-green-800"
            >
              View All Products <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </Container>
      </section>

      {/* Partners - a continuously moving strip */}
      <section className="py-14">
        <Container>
          <p className="flex items-center justify-center gap-2 text-center text-sm font-semibold uppercase tracking-wide text-ink-400">
            <Users className="h-4 w-4" /> Our Designated Partners &amp; Partners
          </p>
        </Container>
        <Marquee seconds={30} className="mt-6">
          {PARTNER_NAMES.map((name) => (
            <span
              key={name}
              className="mx-2 rounded-full border border-brand-green-100 bg-surface px-5 py-2.5 text-sm font-medium text-brand-green-800 shadow-card"
            >
              {name}
            </span>
          ))}
        </Marquee>
        <div className="mt-6 text-center">
          <a href="#partners" className="text-sm font-semibold text-brand-green-700 hover:text-brand-green-800">
            Meet our partners <ArrowRight className="ml-1 inline h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-brand-green-800 py-16">
        <Container className="flex flex-col items-center justify-between gap-6 text-center lg:flex-row lg:text-left">
          <Reveal>
            <h2 className="text-2xl font-bold text-white sm:text-3xl">Ready to taste the difference?</h2>
            <p className="mt-2 max-w-xl text-brand-green-100">
              Get in touch for pricing, bulk orders, or to plan a visit to the farm — we would love to hear from you.
            </p>
          </Reveal>
          <a
            href="#contact"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-gold-400 px-7 py-3 text-sm font-semibold text-brand-green-950 shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-gold-300"
          >
            Contact Us <ArrowRight className="h-4 w-4" />
          </a>
        </Container>
      </section>
    </section>
  );
}
